import { reactive, onUnmounted } from 'vue';
import { useTaskActions } from 'dashboard/modules/tasks/composables/useTaskActions';

const MIN_DRAG_DISTANCE = 4; // px — evita drag acidental em cliques simples

export function useTaskDayDrag({ dayCellRefs, priorityBarStyle }) {
  const { updateTask } = useTaskActions();

  const dragState = reactive({
    draggingId: null,
    targetDayKey: null,
  });

  let activeDrag = null;
  let clone = null;

  function getTargetCell(clientX, clientY) {
    const cells = dayCellRefs.value;
    let result = null;
    cells.forEach((el, dayKey) => {
      if (!el || result) return;
      const rect = el.getBoundingClientRect();
      if (
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom
      ) {
        result = dayKey;
      }
    });
    return result;
  }

  function createClone(task, event, cardEl) {
    const rect = cardEl.getBoundingClientRect();
    const { className, style } = priorityBarStyle(task);

    const el = document.createElement('div');
    el.className = [
      'fixed',
      'z-[9999]',
      'pointer-events-none',
      'rounded',
      'px-1',
      'text-xs',
      'shadow-lg',
      'opacity-90',
      'text-white',
      'truncate',
      className,
    ]
      .filter(Boolean)
      .join(' ');
    Object.assign(el.style, style);
    el.style.width = `${rect.width}px`;
    el.style.height = `${rect.height}px`;
    el.style.left = `${event.clientX - activeDrag.offsetX}px`;
    el.style.top = `${event.clientY - activeDrag.offsetY}px`;
    el.textContent = task.title;

    document.body.appendChild(el);

    return el;
  }

  function onMousemove(event) {
    if (!activeDrag) return;

    const { startX, startY } = activeDrag;
    const moved =
      Math.abs(event.clientX - startX) > MIN_DRAG_DISTANCE ||
      Math.abs(event.clientY - startY) > MIN_DRAG_DISTANCE;

    if (!moved && !activeDrag.started) return;

    if (!activeDrag.started) {
      activeDrag.started = true;
      dragState.draggingId = activeDrag.task.id;
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
      clone = createClone(activeDrag.task, event, activeDrag.cardEl);
    }

    clone.style.left = `${event.clientX - activeDrag.offsetX}px`;
    clone.style.top = `${event.clientY - activeDrag.offsetY}px`;

    const targetDayKey = getTargetCell(event.clientX, event.clientY);
    activeDrag.targetDayKey = targetDayKey;
    dragState.targetDayKey = targetDayKey;
  }

  function onMouseup() {
    if (!activeDrag) return;

    document.removeEventListener('mousemove', onMousemove);
    document.removeEventListener('mouseup', onMouseup);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    if (clone) {
      clone.remove();
      clone = null;
    }

    dragState.draggingId = null;
    dragState.targetDayKey = null;

    if (!activeDrag.started) {
      activeDrag = null;
      return;
    }

    const { task, targetDayKey } = activeDrag;
    activeDrag = null;

    if (!targetDayKey) return;

    const original = new Date(task.due_date * 1000);
    const [year, month, day] = targetDayKey.split('-').map(Number);
    const newDate = new Date(original);
    // targetDayKey vem de dateKey() (dashboard/modules/tasks/components/
    // TaskCalendarView.vue), calculado em horário local — setFullYear (não
    // setUTCFullYear) mantém a hora-do-dia local original ao trocar de dia.
    newDate.setFullYear(year, month - 1, day);

    if (newDate.getTime() === original.getTime()) return;

    updateTask({
      taskId: task.id,
      due_date: newDate.toISOString(),
    });
  }

  function onTaskMousedown(event, task, cardEl) {
    if (activeDrag) {
      document.removeEventListener('mousemove', onMousemove);
      document.removeEventListener('mouseup', onMouseup);
      if (clone) {
        clone.remove();
        clone = null;
      }
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      dragState.draggingId = null;
      dragState.targetDayKey = null;
      activeDrag = null;
    }

    event.preventDefault();

    const rect = cardEl.getBoundingClientRect();
    activeDrag = {
      task,
      cardEl,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      started: false,
      targetDayKey: null,
    };

    document.addEventListener('mousemove', onMousemove);
    document.addEventListener('mouseup', onMouseup);
  }

  onUnmounted(() => {
    document.removeEventListener('mousemove', onMousemove);
    document.removeEventListener('mouseup', onMouseup);
    if (clone) {
      clone.remove();
      clone = null;
    }
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    activeDrag = null;
  });

  return { onTaskMousedown, dragState };
}
